import React, { useState, useEffect } from 'react';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { generateStringFunctionSql } from '../utils/sqlGenerator';

interface StringFunctionBuilderProps {
  columnName: string;
  onSqlGenerate: (sql: string) => void;
}

interface FunctionParam {
  name: string;
  label: string;
  type: 'number' | 'text';
  default: string | number;
}

interface StringFunction {
  value: string;
  label: string;
  params: FunctionParam[];
  example: string;
}

const STRING_FUNCTIONS: StringFunction[] = [
  { value: 'UPPER', label: 'UPPER - Convert to uppercase', params: [], example: "UPPER('hello') → 'HELLO'" },
  { value: 'LOWER', label: 'LOWER - Convert to lowercase', params: [], example: "LOWER('HELLO') → 'hello'" },
  { value: 'TRIM', label: 'TRIM - Remove leading/trailing spaces', params: [], example: "TRIM('  hello  ') → 'hello'" },
  { value: 'LTRIM', label: 'LTRIM - Remove leading spaces', params: [], example: "LTRIM('  hello') → 'hello'" },
  { value: 'RTRIM', label: 'RTRIM - Remove trailing spaces', params: [], example: "RTRIM('hello  ') → 'hello'" },
  { value: 'LENGTH', label: 'LENGTH - Get string length', params: [], example: "LENGTH('hello') → 5" },
  {
    value: 'SUBSTRING',
    label: 'SUBSTRING - Extract portion of string',
    params: [
      { name: 'start', label: 'Start Position', type: 'number', default: 1 },
      { name: 'length', label: 'Length', type: 'number', default: 10 },
    ],
    example: "SUBSTRING('Hello World', 1, 5) → 'Hello'"
  },
  {
    value: 'CONCAT',
    label: 'CONCAT - Combine with another value',
    params: [
      { name: 'value', label: 'Value to Append', type: 'text', default: '' },
    ],
    example: "CONCAT('Hello', ' World') → 'Hello World'"
  },
  {
    value: 'REPLACE',
    label: 'REPLACE - Replace text',
    params: [
      { name: 'from', label: 'Find Text', type: 'text', default: '' },
      { name: 'to', label: 'Replace With', type: 'text', default: '' },
    ],
    example: "REPLACE('Hello', 'llo', 'y') → 'Hey'"
  },
  {
    value: 'REGEXP_REPLACE',
    label: 'REGEXP_REPLACE - Replace using regex',
    params: [
      { name: 'pattern', label: 'Regex Pattern', type: 'text', default: '' },
      { name: 'replacement', label: 'Replacement Text', type: 'text', default: '' },
    ],
    example: "REGEXP_REPLACE('abc123', '[0-9]', 'X') → 'abcXXX'"
  },
  {
    value: 'LEFT',
    label: 'LEFT - Get leftmost N characters',
    params: [
      { name: 'n', label: 'Number of Characters', type: 'number', default: 5 },
    ],
    example: "LEFT('Hello', 3) → 'Hel'"
  },
  {
    value: 'RIGHT',
    label: 'RIGHT - Get rightmost N characters',
    params: [
      { name: 'n', label: 'Number of Characters', type: 'number', default: 5 },
    ],
    example: "RIGHT('Hello', 3) → 'llo'"
  },
];

export const StringFunctionBuilder: React.FC<StringFunctionBuilderProps> = ({
  columnName,
  onSqlGenerate,
}) => {
  const [selectedFunction, setSelectedFunction] = useState('UPPER');
  const [parameters, setParameters] = useState<Record<string, any>>({});

  const currentFunction = STRING_FUNCTIONS.find(f => f.value === selectedFunction);

  useEffect(() => {
    const sql = generateStringFunctionSql(columnName, selectedFunction, parameters);
    onSqlGenerate(sql);
  }, [selectedFunction, parameters, columnName, onSqlGenerate]);

  const handleParameterChange = (paramName: string, value: any) => {
    setParameters(prev => ({ ...prev, [paramName]: value }));
  };

  return (
    <div className="space-y-4">
      <div>
        <Label>String Function</Label>
        <Select value={selectedFunction} onValueChange={setSelectedFunction}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STRING_FUNCTIONS.map((func) => (
              <SelectItem key={func.value} value={func.value}>
                {func.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {currentFunction && currentFunction.params.length > 0 && (
        <div className="space-y-3">
          {currentFunction.params.map((param) => (
            <div key={param.name}>
              <Label>{param.label}</Label>
              <Input
                type={param.type === 'number' ? 'number' : 'text'}
                value={parameters[param.name] ?? param.default}
                onChange={(e) => handleParameterChange(
                  param.name,
                  param.type === 'number' ? parseInt(e.target.value) || 0 : e.target.value
                )}
              />
            </div>
          ))}
        </div>
      )}

      {currentFunction && (
        <div className="text-sm text-muted-foreground">
          <strong>Example:</strong>
          <div className="mt-1">{currentFunction.example}</div>
        </div>
      )}
    </div>
  );
};
